//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import GalleryImage from '@images/icons/gallery.svg';
import Cuid from '@models/Cuid';
import { PostType } from '@models/PostType';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import {
  getGalleryText,
  getTwitterGalleryText,
} from '@uia/rights/gallery/GalleryUtils';
import { getIsRenderedWithinAnotherWorkflow } from '@uia/rights/selectors/getUIARights';
import { getRightsRequestDetailsWithSyndicationCheck } from '@uia/rights/utils/rightsRequestUtils';

export interface RightsGalleryTextProps {
  cuid: Cuid;
}

export const RightsGalleryText = ({ cuid }: RightsGalleryTextProps) => {
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );

  const {
    data: metadata,
    isError: isErrorMetadata,
    isPending: isPendingMetadata,
  } = useMediaMetadataQuery(cuid);

  if (isPendingMetadata || isErrorMetadata) {
    return null;
  }

  const {
    socialDetails: { postType, twitterDetails },
  } = metadata;

  const { status } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  const isGalleryPost = postType === PostType.Gallery;
  const isTwitterGallery = (twitterDetails?.twitterMediaCount ?? 0) > 1;

  if (!isGalleryPost && !isTwitterGallery) {
    return <></>;
  }

  return (
    <Wrapper>
      <Image src={GalleryImage} />
      &nbsp;
      {isGalleryPost && (
        <span>{getGalleryText(status, isRenderedWithinAnotherWorkflow)}</span>
      )}
      {isTwitterGallery && (
        <span>
          {getTwitterGalleryText(status, isRenderedWithinAnotherWorkflow)}
        </span>
      )}
    </Wrapper>
  );
};

const Image = styled.img(
  ({ theme }) => `
    width: ${theme.spacings[3]};
    height: auto;
  `,
);

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: start;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);
